import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import TrainingPeek from "@/components/TrainingPeek";
import { statusClass, ATTENDANCE_TEXT_CLASS } from "@/lib/status";
import { Card } from "@/components/ui/card";
import Paginator, { usePaged } from "@/components/Paginator";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/dates";
import { Badge } from "@/components/ui/badge";
import { Plus, Download } from "lucide-react";
import RegisterFilters from "@/components/RegisterFilters";
import SortableTh, { useSorted } from "@/components/SortableTh";
import { TableSkeleton } from "@/components/Skeletons";
import { useRegisterFilters } from "@/hooks/useRegisterFilters";
import { useAuth, canWrite } from "@/context/AuthContext";

const MATCHED_SHOWN = 5;

// Sorted here rather than in SQL, for the same reason the paging is on the client: the
// server has already narrowed the set and this screen is holding all of it.
const SORT_COLUMNS = {
  date: "training_date",
  title: "title",
  category: "category",
  type: "training_type",
  unit: (t) => (t.unit_names || []).join(", ") || t.unit_name,
  trainer: "trainer_name",
  hours: (t) => Number(t.duration_hours) || 0,
  status: "workflow_status",
};

export default function Trainings() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [items, setItems] = useState(null);   // null = not arrived yet, [] = arrived empty
  const nav = useNavigate();

  // The filters, the URL they live in and the string handed to the server: one
  // definition, shared with Reports. See hooks/useRegisterFilters.js.
  const filters = useRegisterFilters();
  const { f, write, search, masters } = filters;

  const askingAboutPeople =
    f.employees.length > 0 || f.depts.length > 0 || f.attendeeUnits.length > 0 || f.attendance.length > 0;

  useEffect(() => {
    let live = true;
    setItems(null);
    api.get(`/trainings?${search}`).then((r) => { if (live) setItems(r.data); });
    return () => { live = false; };
  }, [search]);

  const rows = items || [];
  const sorted = useSorted(rows, SORT_COLUMNS, f.sort);
  const paged = usePaged(sorted);

  const exportUrl = (ext) => `${API}/exports/trainings.${ext}${search ? `?${search}` : ""}`;
  const onSort = (next) => write({ sort: next });

  return (
    <div className="space-y-5" data-testid="trainings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">Training records</div>
          <h2 className="font-heading font-bold text-3xl mt-1">All Trainings</h2>
          <p className="text-sm text-muted-foreground mt-1">Master list of every training conducted, in-progress or requested.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="export-trainings-pdf" onClick={() => window.open(exportUrl("pdf"))}>
            <Download className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" data-testid="export-trainings-xlsx" onClick={() => window.open(exportUrl("xlsx"))}>
            <Download className="w-4 h-4 mr-1.5" /> Excel
          </Button>
          {writable && <Button onClick={() => nav("/trainings/new")} data-testid="new-training-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Training
          </Button>}
        </div>
      </div>

      <RegisterFilters
        state={filters} count={rows.length} noun="training"
        footer={filters.pills.length > 0 && (
          <p className="text-xs text-muted-foreground -mt-1">
            The PDF and Excel buttons follow these filters, and this page's address carries
            them — copy it out of the bar to send somebody exactly this view.
          </p>
        )} />

      {items === null ? (
        <TableSkeleton rows={8} cols={askingAboutPeople ? 9 : 8} testid="trainings-skeleton" />
      ) : (
        <Card className="border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-zebra" data-testid="trainings-table">
              <thead className="bg-muted/70 border-b border-border">
                <tr className="text-left">
                  <SortableTh sortKey="date" sort={f.sort} onSort={onSort}>Date</SortableTh>
                  <SortableTh sortKey="title" sort={f.sort} onSort={onSort}>Title</SortableTh>
                  <SortableTh sortKey="category" sort={f.sort} onSort={onSort}>Category</SortableTh>
                  <SortableTh sortKey="type" sort={f.sort} onSort={onSort}>Type</SortableTh>
                  <SortableTh sortKey="unit" sort={f.sort} onSort={onSort}>Unit</SortableTh>
                  <SortableTh sortKey="trainer" sort={f.sort} onSort={onSort}>Trainer</SortableTh>
                  <SortableTh sortKey="hours" sort={f.sort} onSort={onSort} align="right">Hours</SortableTh>
                  <SortableTh sortKey="status" sort={f.sort} onSort={onSort}>Status</SortableTh>
                  {askingAboutPeople && <th className="px-4 py-3 font-medium">Attended by</th>}
                </tr>
              </thead>
              <tbody>
                {paged.slice.map((t) => (
                  // The row opens a panel beside the list; the title goes to the page.
                  // Reading one training used to cost the whole register — filters,
                  // scroll, the row you were on.
                  <tr key={t.id} className={`border-b border-border last:border-0 hover:bg-muted/50 cursor-pointer ${f.open === t.id ? "bg-primary/5" : ""}`}
                      onClick={() => write({ open: t.id })} data-testid={`training-row-${t.id}`}>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(t.training_date) || "—"}</td>
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/trainings/${t.id}`} onClick={(e) => e.stopPropagation()}
                            className="hover:text-primary hover:underline underline-offset-2"
                            data-testid={`training-link-${t.id}`}>{t.title}</Link>
                    </td>
                    <td className="px-4 py-3">{t.category}</td>
                    <td className="px-4 py-3"><Badge variant="outline">{t.training_type}</Badge></td>
                    <td className="px-4 py-3">{(t.unit_names || []).join(", ") || t.unit_name}</td>
                    <td className="px-4 py-3">{t.trainer_name || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{t.duration_hours}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusClass(t.workflow_status)}`}>{t.workflow_status}</span>
                      {t.workflow_status === "Completed" && !t.has_attendance_sheet && (
                        <span className="block mt-1 text-[11px] text-amber-700 dark:text-amber-400 whitespace-nowrap" data-testid={`sheet-missing-${t.id}`}>Sheet missing</span>
                      )}
                    </td>
                    {askingAboutPeople && (
                      <td className="px-4 py-3 min-w-[13rem]" data-testid={`matched-${t.id}`}>
                        <ul className="space-y-0.5">
                          {(t.matched_participants || []).slice(0, MATCHED_SHOWN).map((p) => (
                            <li key={p.employee_id} className="text-xs">
                              {p.full_name}
                              <span className={`ml-1.5 whitespace-nowrap ${ATTENDANCE_TEXT_CLASS[p.attendance_status] || "text-muted-foreground"}`}>
                                {p.attendance_status}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {(t.matched_participants || []).length > MATCHED_SHOWN && (
                          <div className="text-xs text-muted-foreground mt-1" data-testid={`matched-more-${t.id}`}>
                            +{t.matched_participants.length - MATCHED_SHOWN} more — open the training to see everyone
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={askingAboutPeople ? 9 : 8} className="px-4 py-12 text-center">
                    <div className="text-muted-foreground">
                      {filters.pills.length ? "No trainings match these filters." : "No trainings yet."}
                    </div>
                    {filters.pills.length ? (
                      <Button variant="outline" size="sm" className="mt-3" onClick={filters.clearAll}>Clear the filters</Button>
                    ) : writable ? (
                      <Button size="sm" className="mt-3" onClick={() => nav("/trainings/new")}>
                        <Plus className="w-4 h-4 mr-1.5" /> Add the first training
                      </Button>
                    ) : null}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Paginator paged={paged} label="trainings" />
        </Card>
      )}

      <TrainingPeek id={f.open} onClose={() => write({ open: "" })} />
    </div>
  );
}
